import { useEffect, useRef, useState } from "react";
import { Img, Lines, Reveal, WaIcon, WA_URL } from "../lib/site";
import Phone from "./Phone";

const AUDIO_SRC = "/audio/demo-llamada.mp3";

function DemoAudio() {
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const a = new Audio(AUDIO_SRC);
    a.preload = "metadata";
    const onMeta = () => setReady(true);
    const onEnd = () => setPlaying(false);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    audioRef.current = a;
    return () => {
      a.pause();
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  if (!ready) return null;

  const toggle = () => {
    const a = audioRef.current;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={playing}
      data-testid="hero-audio-button"
      className="btn btn-line"
    >
      <svg viewBox="0 0 16 16" className="h-4 w-4" fill="currentColor" aria-hidden="true">
        {playing ? (
          <path d="M3.5 2.5h3v11h-3zM9.5 2.5h3v11h-3z" />
        ) : (
          <path d="M4 2.5l9 5.5-9 5.5z" />
        )}
      </svg>
      <span>{playing ? "Pausar la llamada" : "Escucha una llamada de ejemplo"}</span>
    </button>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative flex min-h-screen items-center overflow-hidden bg-noche text-crema" data-testid="hero">
      <div className="plx-wrap" data-plx="0.35">
        <div className="plx-in">
          <Img
            eager
            src="https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&q=80&w=2000"
            alt="Interior de restaurante con luz cálida durante el servicio"
            name="portada-cocina"
            className="hero-zoom h-full w-full object-cover"
          />
        </div>
      </div>
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(90deg, rgba(6,23,19,0.88) 0%, rgba(6,23,19,0.45) 100%)",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[#061713]/25" aria-hidden="true" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-5 pb-24 pt-36 md:px-8 lg:grid-cols-2 lg:gap-8 lg:pt-28">
        <div>
          <Reveal>
            <p className="mono-tag mb-8">Agentes de voz para restaurantes</p>
          </Reveal>
          <Lines
            as="h1"
            className="font-display display-hero text-5xl leading-[0.98] sm:text-6xl lg:text-7xl"
            lines={[
              "Tú, la cocina.",
              <span key="l2" className="serif-accent text-salvia">Nosotros, el teléfono.</span>,
            ]}
          />
          <Reveal delay={180}>
            <p className="mt-8 max-w-md text-base leading-relaxed text-crema/80 md:text-lg">
              Kanvia contesta las llamadas de tu restaurante, toma el pedido
              completo y lo manda a cocina escrito. A cualquier hora, sin que
              nadie tenga que soltar lo que está haciendo.
            </p>
          </Reveal>
          <Reveal delay={270}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#contacto" className="btn btn-solid" data-testid="hero-demo-button">
                Empezar ahora
              </a>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Escríbenos por WhatsApp"
                data-testid="hero-whatsapp-button"
                className="btn btn-line"
              >
                <WaIcon className="h-4 w-4" />
                <span>WhatsApp</span>
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={200} className="flex flex-col items-center gap-8 lg:items-end lg:pr-10">
          <Phone />
          <DemoAudio />
        </Reveal>
      </div>
    </section>
  );
}
